import React, { useCallback, useEffect, useState } from 'react';
import { UserRound, Mail, Shield, Lock, Loader2 } from 'lucide-react';
import { useAuth, useToast } from '@/shared/contexts';
import { superAdminApi } from '@/pages/SuperAdmin/api/superAdminApi';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';

const Field: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value
}) => (
  <div>
    <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
      {label}
    </p>
    <p className="mt-1 text-sm font-medium text-text-primary">
      {value?.trim() ? value : '—'}
    </p>
  </div>
);

function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  const pick = (value: unknown): string | null => {
    if (value == null) return null;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ');
    }
    if (typeof value === 'object' && value !== null && 'message' in value) {
      return pick((value as { message: unknown }).message);
    }
    return null;
  };
  return pick(data) || fallback;
}

export const SuperAdminProfilePage: React.FC = () => {
  const { user, accessToken } = useAuth();
  const { notify } = useToast();

  const [codeIsSet, setCodeIsSet] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentUnlockCode, setCurrentUnlockCode] = useState('');
  const [unlockCode, setUnlockCode] = useState('');
  const [confirmUnlockCode, setConfirmUnlockCode] = useState('');

  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    user?.business_name ||
    'Super Admin';

  const initials = (
    user?.first_name?.[0] ||
    user?.email?.[0] ||
    'S'
  ).toUpperCase();

  const loadSettings = useCallback(async () => {
    if (!accessToken) return;
    setLoadingSettings(true);
    setError(null);
    try {
      const res = await superAdminApi.getOrderUnlockSettings(accessToken);
      setCodeIsSet(Boolean(res.data.isSet));
      setUpdatedAt(res.data.updatedAt);
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to load order creation settings'));
    } finally {
      setLoadingSettings(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleSaveUnlockCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    const next = unlockCode.trim();
    if (next.length < 4) {
      setError('Order creation code must be at least 4 characters');
      return;
    }
    if (next !== confirmUnlockCode.trim()) {
      setError('New order creation code and confirmation do not match');
      return;
    }
    if (codeIsSet && !currentUnlockCode.trim()) {
      setError('Enter your current order creation code to change it');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await superAdminApi.setOrderUnlockCode(accessToken, {
        unlockCode: next,
        ...(codeIsSet
          ? { currentUnlockCode: currentUnlockCode.trim() }
          : {})
      });
      setCodeIsSet(Boolean(res.data.isSet));
      setUpdatedAt(res.data.updatedAt);
      setCurrentUnlockCode('');
      setUnlockCode('');
      setConfirmUnlockCode('');
      notify('success', 'Order creation code saved');
    } catch (err: unknown) {
      setError(extractApiError(err, 'Failed to save order creation code'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
          Settings
        </h2>
        <p className="mt-1 text-sm text-text-tertiary">
          Account details and order creation code for this super admin
        </p>
      </div>

      <div className="rounded-2xl border border-border-primary bg-background-card p-6 shadow-sm sm:p-8">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#006B82] text-2xl font-semibold text-white">
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="h-full w-full rounded-2xl object-cover"
              />
            ) : (
              initials
            )}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-xl font-semibold text-text-primary">
              {displayName}
            </h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-text-tertiary">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{user?.email || '—'}</span>
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#006B82]/10 px-2.5 py-1 text-xs font-medium text-[#006B82]">
              <Shield className="h-3.5 w-3.5" />
              Super Admin
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border-primary bg-background-card shadow-sm overflow-hidden">
        <div className="border-b border-border-secondary px-5 py-4 sm:px-6">
          <h3 className="flex items-center gap-2 font-semibold text-text-primary">
            <UserRound className="h-4 w-4 text-[#006B82]" />
            Account details
          </h3>
        </div>
        <div className="grid gap-6 p-5 sm:grid-cols-2 sm:p-6">
          <Field label="First name" value={user?.first_name} />
          <Field label="Last name" value={user?.last_name} />
          <Field label="Email" value={user?.email} />
          <Field label="Phone" value={user?.phone} />
          <Field label="Business name" value={user?.business_name} />
          <Field label="Status" value={user?.status} />
        </div>
      </div>

      <div className="rounded-2xl border border-border-primary bg-background-card shadow-sm overflow-hidden">
        <div className="border-b border-border-secondary px-5 py-4 sm:px-6">
          <h3 className="flex items-center gap-2 font-semibold text-text-primary">
            <Lock className="h-4 w-4 text-[#006B82]" />
            Order creation code
          </h3>
          <p className="mt-1 text-sm text-text-tertiary">
            Required before placing orders for this account.
          </p>
        </div>

        <div className="space-y-4 p-5 sm:p-6">
          {loadingSettings ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-[#006B82]" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm text-text-secondary">
                <span
                  className={[
                    'inline-flex rounded-full px-2.5 py-1 text-xs font-semibold',
                    codeIsSet
                      ? 'bg-[#006B82]/10 text-[#006B82]'
                      : 'bg-amber-50 text-amber-800'
                  ].join(' ')}
                >
                  {codeIsSet ? 'Configured' : 'Not set'}
                </span>
                {updatedAt && (
                  <span>
                    Updated {new Date(updatedAt).toLocaleString()}
                  </span>
                )}
              </div>

              <form className="space-y-4" onSubmit={handleSaveUnlockCode}>
                {codeIsSet && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-text-tertiary">
                      Current order creation code
                    </label>
                    <Input
                      type="password"
                      autoComplete="off"
                      value={currentUnlockCode}
                      onChange={e => setCurrentUnlockCode(e.target.value)}
                      placeholder="Enter current code"
                      className="bg-background-card"
                      disabled={saving}
                    />
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-tertiary">
                    {codeIsSet
                      ? 'New order creation code'
                      : 'Order creation code'}
                  </label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={unlockCode}
                    onChange={e => setUnlockCode(e.target.value)}
                    placeholder="At least 4 characters"
                    className="bg-background-card"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-text-tertiary">
                    Confirm order creation code
                  </label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={confirmUnlockCode}
                    onChange={e => setConfirmUnlockCode(e.target.value)}
                    placeholder="Re-enter order creation code"
                    className="bg-background-card"
                    disabled={saving}
                  />
                </div>

                {error && <Alert variant="destructive">{error}</Alert>}

                <Button
                  type="submit"
                  className="bg-[#006B82] hover:bg-[#005A6E]"
                  disabled={saving || !unlockCode.trim()}
                >
                  {saving && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {codeIsSet
                    ? 'Update order creation code'
                    : 'Save order creation code'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
